import { useState, useCallback, useRef, useEffect } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GCP_API_KEY;

/**
 * Google Places hook using the Maps JavaScript API (client-side, CORS-safe).
 * Uses legacy AutocompleteService + PlacesService with a pinned SDK version
 * to avoid breaking changes in the latest Google Maps SDK.
 */

// Global promise so we only load the SDK once across all hook instances
let sdkLoadPromise = null;

function loadGoogleMapsSDK() {
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise((resolve, reject) => {
    // Already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(true);
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Missing REACT_APP_GCP_API_KEY'));
      return;
    }

    // Do NOT inject script here. The page loader (@react-google-maps/api)
    // is responsible for script injection. We only wait until it is ready.
    const check = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        clearInterval(check);
        clearTimeout(timeoutId);
        resolve(true);
      }
    }, 100);

    const timeoutId = setTimeout(() => {
      clearInterval(check);
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve(true);
      } else {
        reject(
          new Error(
            'Google Maps SDK not ready. Ensure a single @react-google-maps/api loader is mounted before using Places.'
          )
        );
      }
    }, 15000);
  });

  return sdkLoadPromise;
}

export function useGooglePlaces() {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [placeDetails, setPlaceDetails] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const dummyDiv = useRef(null);

  // Load SDK on mount
  useEffect(() => {
    loadGoogleMapsSDK()
      .then(() => {
        try {
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
          if (!dummyDiv.current) {
            dummyDiv.current = document.createElement('div');
          }
          placesService.current = new window.google.maps.places.PlacesService(
            dummyDiv.current
          );
          setSdkReady(true);
          console.log('[GooglePlaces] SDK ready, services initialized');
        } catch (err) {
          console.error('[GooglePlaces] Service init error:', err);
        }
      })
      .catch((err) => {
        console.error('Google Places SDK load error:', err);
      });
  }, []);

  const searchPlaces = useCallback(
    async (input) => {
      if (!input || input.length < 3) {
        setPredictions([]);
        return;
      }

      // Wait for SDK if not ready
      if (!autocompleteService.current) {
        try {
          await loadGoogleMapsSDK();
          autocompleteService.current =
            new window.google.maps.places.AutocompleteService();
        } catch {
          console.error('Cannot search — Google Maps SDK failed to load');
          return;
        }
      }

      setLoading(true);
      try {
        autocompleteService.current.getPlacePredictions(
          { input, types: ['establishment'] },
          (results, status) => {
            setLoading(false);
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              results
            ) {
              setPredictions(
                results.map((r) => ({
                  place_id: r.place_id,
                  description: r.description,
                  structured_formatting: {
                    main_text:
                      r.structured_formatting?.main_text || r.description,
                    secondary_text:
                      r.structured_formatting?.secondary_text || ''
                  }
                }))
              );
            } else {
              setPredictions([]);
            }
          }
        );
      } catch (error) {
        console.error('Error fetching place predictions:', error);
        setPredictions([]);
        setLoading(false);
      }
    },
    [sdkReady]
  );

  const getPlaceDetails = useCallback(
    async (placeId) => {
      // Wait for SDK if not ready
      if (!placesService.current) {
        try {
          await loadGoogleMapsSDK();
          if (!dummyDiv.current) {
            dummyDiv.current = document.createElement('div');
          }
          placesService.current = new window.google.maps.places.PlacesService(
            dummyDiv.current
          );
        } catch {
          console.error('Cannot get details — Google Maps SDK failed to load');
          return;
        }
      }

      setLoading(true);
      try {
        placesService.current.getDetails(
          {
            placeId,
            fields: ['name', 'formatted_address', 'geometry', 'place_id']
          },
          (place, status) => {
            setLoading(false);
            if (
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              place
            ) {
              setPlaceDetails({
                place_id: placeId,
                name: place.name,
                formatted_address: place.formatted_address,
                geometry: {
                  location: {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                  }
                }
              });
            } else {
              console.error('Place details failed with status:', status);
              setPlaceDetails(null);
            }
          }
        );
      } catch (error) {
        console.error('Error fetching place details:', error);
        setPlaceDetails(null);
        setLoading(false);
      }
    },
    [sdkReady]
  );

  const clearPredictions = useCallback(() => setPredictions([]), []);

  return {
    predictions,
    loading,
    placeDetails,
    searchPlaces,
    getPlaceDetails,
    clearPredictions
  };
}
